/**
 * 목록 지역 필터 — 주소에서 (시·도 짧은 이름, 시·군·구) 추출 + 2단계 트리 (2026-09-22).
 *
 * - 지도 API(/properties/map/)는 region_si/region_gu 를 주지 않으므로 address 로 판정한다.
 *   (백엔드 models._extract_region_from_address 와 같은 규칙 + 짧은 표기·세종 행복도시 보정)
 * - 표기는 짧은 이름(대구·경기…), 순서는 대구 우선 → SIDO_ORDER.
 * - 시·군·구는 주소 두 번째 어절(-시/-군/-구). '화성특례시' → '화성시'. 세종 등 없으면 null.
 */

export const SIDO_ORDER = [
  '대구', '서울', '부산', '인천', '광주', '대전', '울산', '세종',
  '경기', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주',
] as const;
export type Sido = (typeof SIDO_ORDER)[number];

// 주소 첫 어절 → 짧은 이름
const SIDO_ALIAS: Record<string, Sido> = {
  서울특별시: '서울', 서울시: '서울', 서울: '서울',
  부산광역시: '부산', 부산시: '부산', 부산: '부산',
  대구광역시: '대구', 대구시: '대구', 대구: '대구',
  인천광역시: '인천', 인천시: '인천', 인천: '인천',
  광주광역시: '광주', 광주: '광주',
  대전광역시: '대전', 대전시: '대전', 대전: '대전',
  울산광역시: '울산', 울산시: '울산', 울산: '울산',
  세종특별자치시: '세종', 세종시: '세종', 세종: '세종', 행정중심복합도시: '세종',
  경기도: '경기', 경기: '경기',
  강원도: '강원', 강원특별자치도: '강원', 강원: '강원',
  충청북도: '충북', 충북: '충북',
  충청남도: '충남', 충남: '충남',
  전라북도: '전북', 전북특별자치도: '전북', 전북: '전북',
  전라남도: '전남', 전남: '전남',
  경상북도: '경북', 경북: '경북',
  경상남도: '경남', 경남: '경남',
  제주특별자치도: '제주', 제주도: '제주', 제주: '제주',
};

export interface RegionOfAddress {
  si: Sido;
  gu: string | null;
}

export const parseRegion = (address: string | null | undefined): RegionOfAddress | null => {
  if (!address) return null;
  const tokens = address.trim().split(/\s+/);
  const si = SIDO_ALIAS[tokens[0]];
  if (!si) return null;
  if (si === '세종') return { si, gu: null };
  const second = tokens[1] ?? '';
  const m = second.match(/^([가-힣]+?)(특례시|시|군|구)$/);
  const gu = m ? `${m[1]}${m[2] === '특례시' ? '시' : m[2]}` : null;
  return { si, gu };
};

export interface RegionGuNode { gu: string; count: number }
export interface RegionSiNode { si: Sido; count: number; gus: RegionGuNode[] }

/**
 * 데이터에 실제 있는 시·도/시·군·구만 트리로. 시·도 = SIDO_ORDER 순,
 * 시·군·구 = 현장 수 많은 순 → 이름순. countSource 를 주면 카운트는 그 목록 기준
 * (다른 필터 적용 후 수), 트리 구성은 항상 all 기준(0개여도 목록에서 사라지지 않게).
 */
export const buildRegionTree = <T extends { address?: string | null }>(
  all: T[],
  countSource: T[] = all,
): RegionSiNode[] => {
  const present = new Map<Sido, Set<string>>();
  all.forEach((p) => {
    const r = parseRegion(p.address);
    if (!r) return;
    if (!present.has(r.si)) present.set(r.si, new Set());
    if (r.gu) present.get(r.si)!.add(r.gu);
  });
  const siCount = new Map<string, number>();
  const guCount = new Map<string, number>();
  countSource.forEach((p) => {
    const r = parseRegion(p.address);
    if (!r) return;
    siCount.set(r.si, (siCount.get(r.si) ?? 0) + 1);
    if (r.gu) guCount.set(`${r.si}|${r.gu}`, (guCount.get(`${r.si}|${r.gu}`) ?? 0) + 1);
  });
  return SIDO_ORDER.filter((si) => present.has(si)).map((si) => ({
    si,
    count: siCount.get(si) ?? 0,
    gus: Array.from(present.get(si)!)
      .map((gu) => ({ gu, count: guCount.get(`${si}|${gu}`) ?? 0 }))
      .sort((a, b) => b.count - a.count || a.gu.localeCompare(b.gu, 'ko')),
  }));
};

/** si 비면 전체, gu 비면 그 시·도 전체 */
export const matchRegion = (address: string | null | undefined, si: string, gu: string): boolean => {
  if (!si) return true;
  const r = parseRegion(address);
  if (!r || r.si !== si) return false;
  if (!gu) return true;
  return r.gu === gu;
};

export const regionLabel = (si: string, gu: string): string =>
  si ? (gu ? `${si} ${gu}` : si) : '';
